import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Role = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Membership {
  org_id: string;
  org_slug: string;
  org_name: string;
  role: Role;
}

interface AuthState {
  token: string | null;
  userId: string | null;
  email: string | null;
  name: string | null;
  isPlatformAdmin: boolean;
  memberships: Membership[];
  /** Currently selected org id; sent as X-Org-Id on every request. */
  activeOrgId: string | null;
  setAuth: (a: Partial<Omit<AuthState, 'setAuth' | 'logout' | 'switchOrg'>>) => void;
  switchOrg: (orgId: string) => void;
  logout: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      userId: null,
      email: null,
      name: null,
      isPlatformAdmin: false,
      memberships: [],
      activeOrgId: null,
      setAuth: (a) => set(a),
      switchOrg: (orgId) => set({ activeOrgId: orgId }),
      logout: () =>
        set({
          token: null,
          userId: null,
          email: null,
          name: null,
          isPlatformAdmin: false,
          memberships: [],
          activeOrgId: null,
        }),
    }),
    { name: 'iotech-signage-auth-v2' }
  )
);

export function activeMembership(): Membership | null {
  const { memberships, activeOrgId } = useAuth.getState();
  return memberships.find((m) => m.org_id === activeOrgId) ?? null;
}

export function activeRole(): Role | null {
  const m = activeMembership();
  if (m) return m.role;
  // Platform admins acting on an org they're not a member of get implicit owner
  return useAuth.getState().isPlatformAdmin ? 'owner' : null;
}

export function canManageMembers(): boolean {
  const r = activeRole();
  return r === 'owner' || r === 'admin';
}

export function canEdit(): boolean {
  const r = activeRole();
  return r === 'owner' || r === 'admin' || r === 'editor';
}
